<template>
  <AIVDialog
    v-model="isVisible"
    title="Submit Training Completion"
    width="650px"
    height="auto"
    class="training-submission-modal"
  >
    <div class="modal-content">
      <!-- Instructions -->
      <div class="row">
        <div class="col-md-12 mb-3">
          <div class="alert alert-info">
            <i class="fa fa-info-circle"></i>
            Please confirm that you have reviewed all of the Client Specific Training documents before submitting your completion request for approval.
          </div>
        </div>
      </div>

      <!-- Document Checkboxes -->
      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">
            <strong>Training Documents *</strong>
          </label>
          <div class="documents-checklist">
            <div
              v-for="doc in documents"
              :key="doc.id"
              class="document-checkbox-item"
            >
              <el-checkbox
                v-model="documentCheckboxes[doc.id]"
                @change="clearValidationError"
              >
                <i :class="doc.is_link ? 'fa fa-link' : 'fa fa-file'" class="mr-2"></i>
                <span class="document-name">{{ doc.original_filename || doc.url || 'Document' }}</span>
              </el-checkbox>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation Error -->
      <div class="row" v-if="validationError">
        <div class="col-md-12 mb-3">
          <div class="alert alert-danger">
            <i class="fa fa-times-circle"></i>
            {{ validationError }}
          </div>
        </div>
      </div>

      <!-- Confirmation Message -->
      <div class="row" v-if="allDocumentsChecked">
        <div class="col-md-12 mb-3">
          <div class="alert alert-success">
            <i class="fa fa-check-circle"></i>
            All documents have been reviewed. You may now submit your training completion request.
          </div>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <button
          @click="submitRequest"
          :disabled="isSubmitting"
          class="btn btn-primary"
        >
          <i class="fa fa-paper-plane"></i>
          {{ isSubmitting ? 'Submitting...' : 'Submit for Approval' }}
        </button>
        <button
          @click="closeModal"
          class="btn btn-secondary ml-2"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
export default {
  name: "TrainingSubmissionModal",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    documents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      documentCheckboxes: {},
      validationError: '',
      isSubmitting: false
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    allDocumentsChecked() {
      if (!this.documents || this.documents.length === 0) {
        return false;
      }
      return this.documents.every(doc => this.documentCheckboxes[doc.id] === true);
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // Reset checkboxes when modal opens
        this.initializeCheckboxes();
      }
    }
  },
  methods: {
    initializeCheckboxes() {
      this.documentCheckboxes = {};
      if (this.documents && this.documents.length > 0) {
        this.documents.forEach(doc => {
          this.$set(this.documentCheckboxes, doc.id, false);
        });
      }
    },

    clearValidationError() {
      this.validationError = '';
    },

    async submitRequest() {
      // Validate that all documents are checked
      if (!this.allDocumentsChecked) {
        this.validationError = 'Please confirm that you have reviewed all training documents by checking all boxes above.';
        this.$notifyError(this.validationError);
        return;
      }

      if (this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;

      try {
        // Emit submit event to parent component
        this.$emit('submit');
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Error submitting request';
        this.$notifyError(errorMessage);
        this.isSubmitting = false;
      }
    },

    closeModal() {
      this.isVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.documentCheckboxes = {};
      this.validationError = '';
      this.isSubmitting = false;
    }
  }
};
</script>

<style scoped>
.training-submission-modal .modal-content {
  padding: 0;
}

.documents-checklist {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  background-color: #f9f9f9;
  max-height: 300px;
  overflow-y: auto;
}

.document-checkbox-item {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.document-checkbox-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.1);
}

.document-checkbox-item:last-child {
  margin-bottom: 0;
}

.document-name {
  color: #333;
  font-size: 0.95rem;
}

.alert {
  border-radius: 8px;
  padding: 1rem;
  margin: 0;
}

.alert-info {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  color: #004085;
}

.alert-success {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.alert i {
  margin-right: 0.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-size: 0.95rem;
}

.btn {
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #409eff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #66b1ff;
}

.btn-primary:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #909399;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #a6a9ad;
}

.btn i {
  margin-right: 0.5rem;
}
</style>
